import { assertNotEmpty } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  type FPlusExpressionValue,
  isNotRelevantField,
  type SimpleValueSetupField,
  type SimpleValueSetupFieldValue,
} from '../../types/templateModel'
import {
  generateEmptyDynamicFunctionValue,
  generateEmptyFieldValue,
} from '../../uiModels/createEmptyFieldHelpers'
import { SimpleFieldValueDynamicFunction } from '../dynamicFunction/SimpleFieldValueDynamicFunction'

type Props = {
  field: SimpleValueSetupField
}

export const TemplateFieldDynamicFunctions = ({ field }: Props) => {
  const { isDraftTemplate } = useTemplateContext()
  const translate = useTranslate()

  const fieldValue = useMemo(
    () =>
      field.value ??
      (generateEmptyFieldValue({
        field,
      }) as SimpleValueSetupFieldValue),
    [field],
  )

  const handleAddDynamicFunction = async () => {
    const createdDynamicFunction = generateEmptyDynamicFunctionValue({
      type: 'dynamic',
      path: field.path,
      functionIdentifier: '',
      // The update functions are regenerated for the dynamic function when the root operation is updating and the
      // visual model is regenerated, so this can just be a no-op
      update: () => {},
      updateRaw: () => {},
      isParentNotRelevant: false,
    })

    await field.update({
      ...fieldValue,
      origin: null,
      dynamicFunctions: [
        ...(fieldValue.dynamicFunctions ?? []),
        createdDynamicFunction,
      ],
    })
  }

  const handleRemoveFunction = (functionIndex: number) => async () => {
    const nextDynamicFunctions = [...(fieldValue.dynamicFunctions ?? [])]

    nextDynamicFunctions?.splice(functionIndex, 1)

    await field.update({
      ...fieldValue,
      dynamicFunctions: nextDynamicFunctions,
      origin: null,
    })
  }

  const handleFunctionChanged = useCallback(
    (functionIndex: number) => (nextFunctionValue: FPlusExpressionValue) => {
      const nextDynamicFunctions = [...(fieldValue.dynamicFunctions ?? [])]
      const changedFunction = fieldValue.dynamicFunctions?.at(functionIndex)

      assertNotEmpty(changedFunction)

      nextDynamicFunctions?.splice(functionIndex, 1, {
        ...changedFunction,
        function: nextFunctionValue,
        origin: null,
      })

      return field.update({
        ...fieldValue,
        dynamicFunctions: nextDynamicFunctions,
        origin: null,
      })
    },
    [field, fieldValue],
  )

  return (
    <>
      {isDraftTemplate && (
        <Button
          className="mb-2 whitespace-nowrap"
          onClick={handleAddDynamicFunction}
          disabled={isNotRelevantField(field)}
          startIcon="PlusCircle"
        >
          {translate(
            'templateViewer.fieldDetailsModal.dynamicFunctions.addDynamicFunction',
          )}
        </Button>
      )}
      <div className="flex flex-col gap-y-2">
        {(field.value?.dynamicFunctions ?? []).map((dynamicFunction, index) => (
          <SimpleFieldValueDynamicFunction
            // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
            key={`${dynamicFunction.path}-${index}`}
            isShowingTitle
            isShowingErrors
            value={dynamicFunction}
            onChange={handleFunctionChanged(index)}
            onRemove={handleRemoveFunction(index)}
          />
        ))}
      </div>
    </>
  )
}
